import { useState, useEffect } from "react";
import api from "../../../utils/api";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormLabel,
    FormHelperText,
    OutlinedInput,
    Box,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    FormControlLabel,
    Checkbox,
    TextField,
    Autocomplete,
} from "@mui/material";

export default function ManualPurchaseModal({ open, onClose }) {
    const [students, setStudents] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loadLists, setLoadLists] = useState(false);
    const [userId, setUserId] = useState("");
    const [courseId, setCourseId] = useState("");
    const [promo, setPromo] = useState("");
    const [status, setStatus] = useState("processed");
    const [createFinanceIncome, setCreateFinanceIncome] = useState(true);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setUserId("");
        setCourseId("");
        setPromo("");
        setStatus("processed");
        setCreateFinanceIncome(true);
        let cancelled = false;
        (async () => {
            setLoadLists(true);
            try {
                const [sRes, cRes] = await Promise.all([
                    api.get("admin/students", { params: { page: 1, perPage: 500 } }),
                    api.get("admin/courses", { params: { page: 1, perPage: 500 } }),
                ]);
                if (!cancelled) {
                    setStudents(sRes.data?.data ?? []);
                    setCourses(cRes.data?.data ?? []);
                }
            } catch {
                if (!cancelled) {
                    setStudents([]);
                    setCourses([]);
                }
            } finally {
                if (!cancelled) setLoadLists(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});
            const body = {
                user_id: Number(userId),
                course_id: Number(courseId),
                status,
            };
            if (status === "processed") {
                body.create_finance_income = createFinanceIncome;
            }
            if (promo.trim()) {
                body.promo = promo.trim();
            }
            await api.post("admin/purchases", body);
            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    const studentLabel = (s) => `${(s.first_name || "").trim()} ${(s.last_name || "").trim()} — ${s.email || ""}`.trim();

    const courseLabel = (c) => (c.name_en || c.name_ru || c.name_hy || `Course #${c.id}`).trim();

    return (
        <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add purchase (manual)</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {loadLists ? (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <>
                            <FormControl fullWidth size="small" error={!!errors.user_id?.[0]}>
                                <Autocomplete
                                    size="small"
                                    options={students}
                                    value={students.find((s) => String(s.id) === String(userId)) || null}
                                    onChange={(_, value) => setUserId(value ? String(value.id) : "")}
                                    getOptionLabel={(option) => studentLabel(option)}
                                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Student"
                                            placeholder="Search student..."
                                            error={!!errors.user_id?.[0]}
                                        />
                                    )}
                                />
                                {errors.user_id && <FormHelperText>{errors.user_id[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth size="small" error={!!errors.course_id?.[0]}>
                                <InputLabel id="manual-purchase-course-label">Course</InputLabel>
                                <Select
                                    labelId="manual-purchase-course-label"
                                    label="Course"
                                    value={courseId}
                                    onChange={(e) => setCourseId(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Select course</em>
                                    </MenuItem>
                                    {courses.map((c) => (
                                        <MenuItem key={c.id} value={String(c.id)}>
                                            {courseLabel(c)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.course_id && (
                                    <FormHelperText>{errors.course_id[0]}</FormHelperText>
                                )}
                            </FormControl>

                            <FormControl fullWidth error={!!errors.promo?.[0]}>
                                <FormLabel htmlFor="manual-purchase-promo">Promo code (optional)</FormLabel>
                                <OutlinedInput
                                    id="manual-purchase-promo"
                                    size="small"
                                    value={promo}
                                    onChange={(e) => setPromo(e.target.value)}
                                    placeholder="Leave empty for full price"
                                />
                                {errors.promo && <FormHelperText>{errors.promo[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth size="small" error={!!errors.status?.[0]}>
                                <InputLabel id="manual-purchase-status-label">Status</InputLabel>
                                <Select
                                    labelId="manual-purchase-status-label"
                                    label="Status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="processed">Processed</MenuItem>
                                    <MenuItem value="canceled">Canceled</MenuItem>
                                    <MenuItem value="expired">Expired</MenuItem>
                                </Select>
                                {errors.status && <FormHelperText>{errors.status[0]}</FormHelperText>}
                            </FormControl>

                            {status === "processed" && (
                                <FormControl fullWidth error={!!errors.create_finance_income?.[0]}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={createFinanceIncome}
                                                onChange={(e) => setCreateFinanceIncome(e.target.checked)}
                                            />
                                        }
                                        label="Create finance income"
                                    />
                                    {errors.create_finance_income && (
                                        <FormHelperText>{errors.create_finance_income[0]}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || loadLists || !userId || !courseId}
                    sx={{ color: "#fff" }}
                >
                    {saving ? <CircularProgress size={24} color="inherit" /> : "Create purchase"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
